import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { MousePointer2, Hand, ChevronDown, Play, RotateCcw, ListOrdered, Slash, ArrowUpRight, Type, Pencil, Eraser, GripVertical } from "lucide-react";
import { useReactFlow } from "@xyflow/react";
import { useCanvasStore, type ActiveTool, type KFNodeData, type CanvasShape } from "@/store/useCanvasStore";
import { NODE_ITEMS, SHAPE_ITEMS, DEFAULT_NODE_KEYS, DEFAULT_SHAPE_KEYS, type NodeItem } from "@/constants/shapeLibrary";
import ElementSearchPanel, { type PanelItem } from "./ElementSearchPanel";
import { THEME } from "@/config/theme";
import { CV } from "@/config/canvasSizing";

const MAX_RECENT = 5;
const RECENT_NODES_KEY  = "kf:recentNodes";
const RECENT_SHAPES_KEY = "kf:recentShapes";

// Which scroll-hint fades to show, given a horizontal scroll box's metrics.
// 1px slack absorbs sub-pixel rounding so a fully-scrolled bar reads as "done".
export function computeToolbarFades(scrollLeft: number, scrollWidth: number, clientWidth: number) {
  const maxScroll = scrollWidth - clientWidth;
  return {
    left:  scrollLeft > 1,
    right: scrollLeft < maxScroll - 1,
  };
}

function loadRecents(key: string): string[] {
  try { return JSON.parse(localStorage.getItem(key) ?? "[]") as string[]; }
  catch { return []; }
}

function saveRecents(key: string, picked: string, prev: string[]): string[] {
  const next = [picked, ...prev.filter((k) => k !== picked)].slice(0, MAX_RECENT);
  localStorage.setItem(key, JSON.stringify(next));
  return next;
}

interface ToolButtonProps {
  title: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolButton({ title, active, onClick, children }: ToolButtonProps) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`
        p-1.5 rounded-md transition-colors outline-none focus-visible:outline-none
        ${active ? THEME.toolbar.buttonActive : THEME.toolbar.buttonInactive}
      `}
    >
      {children}
    </button>
  );
}

// ── Long-press tool group (mobile) ───────────────────────────────────────────
// One button that fronts a small family of tools. Tap fires the primary action;
// long-press opens a flyout with the rest (Figma/Excalidraw-style). Used on
// mobile to shrink the top bar in landscape.
const LONG_PRESS_MS = 450;

interface GroupItem {
  key: string;
  title: string;
  icon: React.ReactNode;
  active: boolean;
  onSelect: () => void;
}

interface ToolGroupButtonProps {
  title: string;
  icon: React.ReactNode;
  active: boolean;
  onPrimary: () => void;
  items: GroupItem[];
}

function ToolGroupButton({ title, icon, active, onPrimary, items, onOpen }: ToolGroupButtonProps & { onOpen?: () => void }) {
  const [open, setOpen] = useState(false);
  // Flyout is portaled to <body> so the toolbar's horizontal scroll container
  // (overflow-x:auto also clips overflow-y) can't cut it off. Coords are the
  // fixed-position anchor under the trigger, captured when the long-press fires.
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressedRef = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || flyoutRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  const handlePointerDown = () => {
    longPressedRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      const r = ref.current?.getBoundingClientRect();
      if (r) setCoords({ top: r.bottom + 4, left: r.left + r.width / 2 });
      setOpen(true);
      onOpen?.();
    }, LONG_PRESS_MS);
  };

  const handlePointerUp = () => {
    clearTimer();
    if (longPressedRef.current) { longPressedRef.current = false; return; }
    onPrimary();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        title={title}
        aria-label={title}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={clearTimer}
        onPointerCancel={clearTimer}
        onContextMenu={(e) => e.preventDefault()}
        className={`
          relative p-2 rounded-md transition-colors outline-none focus-visible:outline-none
          ${active ? THEME.toolbar.buttonActive : THEME.toolbar.buttonInactive}
        `}
      >
        {icon}
        {/* corner caret hinting the long-press flyout */}
        <span className="absolute bottom-[3px] right-[3px] w-0 h-0 border-l-[3px] border-l-transparent border-b-[3px] border-b-current opacity-60" />
      </button>

      {open && coords && createPortal(
        <div
          ref={flyoutRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, transform: "translateX(-50%)" }}
          className={`flex flex-col gap-0.5 ${THEME.toolbar.container} rounded-md p-0.5 shadow-md shadow-black/40 z-50`}
        >
          {items.map((it) => (
            <button
              key={it.key}
              title={it.title}
              aria-label={it.title}
              onClick={() => { it.onSelect(); setOpen(false); }}
              className={`p-0.5 rounded transition-colors ${it.active ? THEME.toolbar.buttonActive : THEME.toolbar.buttonInactive}`}
            >
              {it.icon}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

type OpenPanel = "nodes" | "shapes" | null;

interface PanelButtonProps {
  label: string;
  panelId: NonNullable<OpenPanel>;
  open: boolean;
  onToggle: (id: OpenPanel) => void;
  items: PanelItem[];
  defaultKeys: string[];
  recentKeys: string[];
  onSelect: (key: string) => void;
}

function PanelButton({ label, panelId, open, onToggle, items, defaultKeys, recentKeys, onSelect }: PanelButtonProps) {
  return (
    <div className="relative">
      <button
        title={label}
        aria-label={label}
        aria-expanded={open}
        onClick={() => onToggle(open ? null : panelId)}
        className={`
          flex items-center gap-0.5 px-1.5 py-1.5 rounded-md transition-colors text-xs font-medium
          ${open ? THEME.toolbar.buttonActive : THEME.toolbar.buttonInactive}
        `}
      >
        {label}
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ElementSearchPanel
          items={items}
          defaultKeys={defaultKeys}
          recentKeys={recentKeys}
          onSelect={onSelect}
          onClose={() => onToggle(null)}
        />
      )}
    </div>
  );
}

// Per-type color overrides; falls back to per-category color below
const TYPE_COLOR: Record<string, string> = {
  concept:      "indigo",
  question:     "violet",
  "deep-dive":  "indigo",
  warning:      "amber",
  note:         "amber",
  info:         "sky",
  brain:        "purple",
  process:      "cyan",
  decision:     "purple",
  fork:         "cyan",
  merge:        "cyan",
  branch:       "cyan",
  gateway:      "purple",
  loop:         "cyan",
  trigger:      "yellow",
  event:        "yellow",
  data:         "emerald",
  database:     "emerald",
  storage:      "emerald",
  cache:        "emerald",
  queue:        "orange",
  cloud:        "sky",
  terminal:     "green",
  bug:          "red",
  deploy:       "sky",
  module:       "indigo",
  shield:       "red",
  auth:         "red",
  microservice: "fuchsia",
  pubsub:       "orange",
  api:          "fuchsia",
  webhook:      "orange",
  message:      "blue",
  notification: "blue",
  link:         "blue",
  component:    "indigo",
  actor:        "rose",
  user:         "rose",
  team:         "rose",
  org:          "rose",
  role:         "rose",
  target:       "emerald",
  goal:         "emerald",
  success:      "green",
  error:        "red",
  test:         "teal",
};

const CATEGORY_COLOR: Record<string, string> = {
  Core:   "indigo",
  Flow:   "cyan",
  Data:   "emerald",
  Dev:    "slate",
  Infra:  "slate",
  Events: "blue",
  UI:     "violet",
  People: "rose",
  Ref:    "slate",
  Status: "green",
  Cloud:  "sky",
  Tech:   "slate",
};

// Full class strings so Tailwind can scan them
const BUBBLE: Record<string, string> = {
  indigo:  "bg-indigo-500/20 text-indigo-400",
  violet:  "bg-violet-500/20 text-violet-400",
  purple:  "bg-purple-500/20 text-purple-400",
  cyan:    "bg-cyan-500/20 text-cyan-400",
  emerald: "bg-emerald-500/20 text-emerald-400",
  amber:   "bg-amber-500/20 text-amber-400",
  sky:     "bg-sky-500/20 text-sky-400",
  blue:    "bg-blue-500/20 text-blue-400",
  rose:    "bg-rose-500/20 text-rose-400",
  red:     "bg-red-500/20 text-red-400",
  green:   "bg-green-500/20 text-green-400",
  teal:    "bg-teal-500/20 text-teal-400",
  orange:  "bg-orange-500/20 text-orange-400",
  fuchsia: "bg-fuchsia-500/20 text-fuchsia-400",
  yellow:  "bg-yellow-500/20 text-yellow-400",
  slate:   "bg-zinc-700/50 text-zinc-400",
};

const NODE_ITEM_BY_KEY = new Map<string, NodeItem>(NODE_ITEMS.map((n) => [n.key, n]));

const NODE_PANEL_ITEMS: PanelItem[] = NODE_ITEMS.map((n) => {
  const color = TYPE_COLOR[n.key] ?? CATEGORY_COLOR[n.category] ?? "slate";
  const cls = BUBBLE[color] ?? BUBBLE.slate;
  return {
    key: n.key,
    label: n.label,
    keywords: n.keywords,
    preview: (
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cls}`}>
        {n.icon}
      </div>
    ),
  };
});

const SHAPE_PANEL_ITEMS: PanelItem[] = SHAPE_ITEMS.map((s) => ({
  key: s.key,
  label: s.label,
  keywords: s.keywords,
  preview: s.preview,
}));

export default function Toolbar({ isMobile = false, onGripPointerDown }: { isMobile?: boolean; onGripPointerDown?: (e: React.PointerEvent) => void }) {
  const { activeTool, setActiveTool, setPencilOptionsOpen, addNode, addShape, isRevealMode, isRevealEditMode, enterRevealMode, exitRevealMode, enterRevealEditMode, exitRevealEditMode, revealPointer, rfNodes, rfShapes } = useCanvasStore();
  const maxRevealStep = Math.max(0, ...rfNodes.map(n => (n.data as KFNodeData).revealStep), ...rfShapes.map(n => (n.data as unknown as CanvasShape).revealStep));
  const revealFinished = isRevealMode && maxRevealStep > 0 && revealPointer >= maxRevealStep;
  const { screenToFlowPosition } = useReactFlow();
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);
  const [recentNodes,  setRecentNodes]  = useState<string[]>(() => loadRecents(RECENT_NODES_KEY));
  const [recentShapes, setRecentShapes] = useState<string[]>(() => loadRecents(RECENT_SHAPES_KEY));
  const ref = useRef<HTMLDivElement>(null);

  // Only one toolbar overlay may be open at a time. The Nodes/Shapes picker
  // (openPanel, local state) and the pencil-options popover (store) are separate
  // systems, so opening either must dismiss the other explicitly.
  const selectTool = useCallback((tool: ActiveTool) => {
    setActiveTool(tool);   // setActiveTool also opens pencil options iff tool === "pencil"
    setOpenPanel(null);
  }, [setActiveTool]);

  const openPanelExclusive = useCallback((id: OpenPanel) => {
    setOpenPanel(id);
    if (id !== null) setPencilOptionsOpen(false);
  }, [setPencilOptionsOpen]);

  // Mobile: the bar scrolls horizontally when it overflows; fades on either end
  // signal there's more to reach. Recompute on scroll and on viewport resize
  // (orientation flip changes how many buttons fit).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fades, setFades] = useState({ left: false, right: false });
  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setFades(computeToolbarFades(el.scrollLeft, el.scrollWidth, el.clientWidth));
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    updateFades();
    window.addEventListener("resize", updateFades);
    return () => window.removeEventListener("resize", updateFades);
  }, [isMobile, updateFades]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "a" || e.key === "A") selectTool("arrow");
      if (e.key === "h" || e.key === "H") selectTool("hand");
      if (e.key === "p" || e.key === "P") selectTool("pencil");
      if (e.key === "e" || e.key === "E") selectTool("eraser");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectTool]);

  useEffect(() => {
    if (!openPanel) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpenPanel(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openPanel]);

  const canvasCenter = useCallback(() =>
    screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }),
    [screenToFlowPosition],
  );

  const handleAddNode = useCallback((key: string) => {
    const pos = canvasCenter();
    const item = NODE_ITEM_BY_KEY.get(key);
    addNode(key, pos, item?.iconOverride, item?.label);
    setRecentNodes((prev) => saveRecents(RECENT_NODES_KEY, key, prev));
    setOpenPanel(null);
  }, [addNode, canvasCenter]);

  const handleAddShape = useCallback((key: string) => {
    const pos = canvasCenter();
    addShape(key, pos);
    setRecentShapes((prev) => saveRecents(RECENT_SHAPES_KEY, key, prev));
    setOpenPanel(null);
  }, [addShape, canvasCenter]);

  const navTools: { key: ActiveTool; title: string; icon: React.ReactNode }[] = [
    { key: "arrow", title: "Arrow (A)", icon: <MousePointer2 size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} /> },
    { key: "hand",  title: "Hand (H)",  icon: <Hand size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} /> },
  ];

  const content = (
    <>
      {navTools.map(({ key, title, icon }) => (
        <ToolButton
          key={key}
          title={title}
          active={activeTool === key}
          onClick={() => selectTool(key)}
        >
          {icon}
        </ToolButton>
      ))}

      <div className={`w-px h-4 ${THEME.toolbar.divider} mx-0.5`} />

      <PanelButton
        label="Nodes"
        panelId="nodes"
        open={openPanel === "nodes"}
        onToggle={openPanelExclusive}
        items={NODE_PANEL_ITEMS}
        defaultKeys={DEFAULT_NODE_KEYS}
        recentKeys={recentNodes}
        onSelect={handleAddNode}
      />

      <PanelButton
        label="Shapes"
        panelId="shapes"
        open={openPanel === "shapes"}
        onToggle={openPanelExclusive}
        items={SHAPE_PANEL_ITEMS}
        defaultKeys={DEFAULT_SHAPE_KEYS}
        recentKeys={recentShapes}
        onSelect={handleAddShape}
      />

      <div className={`w-px h-4 ${THEME.toolbar.divider} mx-0.5`} />

      {isMobile ? (
        // Mobile: collapse Line / Free Edge / Pencil into one long-press group so
        // the top bar stays compact in landscape. Tap = pencil, hold = the rest.
        <ToolGroupButton
          title="Draw tools"
          icon={<Pencil size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />}
          active={activeTool === "pencil"}
          onPrimary={() => selectTool("pencil")}
          onOpen={() => setOpenPanel(null)}
          items={[
            { key: "line", title: "Line", active: false, onSelect: () => handleAddShape("line"),
              icon: <Slash size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} /> },
            { key: "free_edge", title: "Free Edge", active: false, onSelect: () => handleAddShape("free_edge"),
              icon: <ArrowUpRight size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} /> },
          ]}
        />
      ) : (
        <>
          <ToolButton title="Line" active={false} onClick={() => handleAddShape("line")}>
            <Slash size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
          </ToolButton>
          <ToolButton title="Free Edge" active={false} onClick={() => handleAddShape("free_edge")}>
            <ArrowUpRight size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
          </ToolButton>
        </>
      )}

      <ToolButton title="Textbox" active={false} onClick={() => handleAddShape("textbox")}>
        <Type size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
      </ToolButton>

      <div className={`w-px h-4 ${THEME.toolbar.divider} mx-0.5`} />

      {!isMobile && (
        <ToolButton title="Pencil (P)" active={activeTool === "pencil"} onClick={() => selectTool("pencil")}>
          <Pencil size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
        </ToolButton>
      )}
      <ToolButton title="Eraser (E)" active={activeTool === "eraser"} onClick={() => selectTool("eraser")}>
        <Eraser size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
      </ToolButton>

      <div className={`w-px h-4 ${THEME.toolbar.divider} mx-0.5`} />

      <ToolButton
        title={revealFinished ? "Restart" : "Present (R)"}
        active={isRevealMode && !revealFinished}
        onClick={() => revealFinished ? enterRevealMode() : isRevealMode ? exitRevealMode() : enterRevealMode()}
      >
        {revealFinished ? <RotateCcw size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} /> : <Play size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />}
      </ToolButton>

      <ToolButton
        title="Edit Sequence"
        active={isRevealEditMode}
        onClick={() => isRevealEditMode ? exitRevealEditMode() : enterRevealEditMode()}
      >
        <ListOrdered size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
      </ToolButton>
    </>
  );

  // Desktop: natural-width flex row. No overflow container so dropdowns that open
  // downward aren't clipped.
  if (!isMobile) {
    return (
      <div
        ref={ref}
        className={`flex items-center gap-0.5 ${THEME.toolbar.container} rounded-lg px-1 py-1 shadow-lg shadow-black/40`}
      >
        {/* Drag grip (floating bar only): the sole element that starts a drag, so
            every tool button stays click-safe. `touch-none` keeps a touch-drag from
            scrolling the page instead of moving the bar. */}
        {onGripPointerDown && (
          <>
            <button
              type="button"
              aria-label="Drag toolbar"
              title="Drag toolbar"
              onPointerDown={onGripPointerDown}
              className={`p-0.5 rounded ${THEME.toolbar.dragHandle} cursor-grab active:cursor-grabbing touch-none outline-none focus-visible:outline-none`}
            >
              <GripVertical size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
            </button>
            <div className={`w-px h-4 ${THEME.toolbar.divider} mx-0.5`} />
          </>
        )}
        {content}
      </div>
    );
  }

  // Mobile: cap to the Panel's width (set in Viewport) and scroll horizontally
  // when buttons overflow, with edge fades hinting there's more to reach.
  return (
    <div
      ref={ref}
      className={`relative w-fit max-w-full ${THEME.toolbar.container} rounded-lg shadow-lg shadow-black/40`}
    >
      <div
        ref={scrollRef}
        data-testid="toolbar-scroll"
        onScroll={updateFades}
        className="flex items-center gap-0.5 px-1 py-1 overflow-x-auto [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {content}
      </div>
      {fades.left && (
        <div
          className={`pointer-events-none absolute inset-y-0 left-0 rounded-l-lg ${THEME.toolbar.fadeLeft}`}
          style={{ width: CV.toolbar.mobileFadeWidth }}
        />
      )}
      {fades.right && (
        <div
          className={`pointer-events-none absolute inset-y-0 right-0 rounded-r-lg ${THEME.toolbar.fadeRight}`}
          style={{ width: CV.toolbar.mobileFadeWidth }}
        />
      )}
    </div>
  );
}
